import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
// QuantitySelectorUpdateEvent is used for JSDoc type annotations
// eslint-disable-next-line no-unused-vars
import { ThemeEvents, CartUpdateEvent, DiscountUpdateEvent, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { getCart, invalidateCart } from '@theme/cart-upsell-state';

/**
 * VBR-220: personalization line item properties that must be stripped off the
 * jacket when its personalization is removed, so the jacket reverts to a plain
 * line with no leftover fulfillment data.
 */
const PERSONALIZATION_PROP_KEYS = new Set([
  '_personalizationGroupId',
  '_personalizationLine',
  '_personalizationRole',
  '_customization',
  '_finalSale',
  '_Mantra',
  '_Placement',
]);

/**
 * @returns {string[]} Discount codes from .cart-discount__pill elements in this component.
 */
function getExistingDiscountCodes(container) {
  const codes = [];
  const pills = container.querySelectorAll('.cart-discount__pill');
  for (const pill of pills) {
    if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
      codes.push(pill.dataset.discountCode);
    }
  }
  return codes;
}

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLLIElement[]} cartItemRows
 * @property {TextComponent} cartTotal
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    // VBR-220: personalized items are added as linked lines sharing a
    // `_personalizationGroupId` — the jacket, the visible mantra line, and the
    // $0 placement line (rendered as a hidden line item so it stays in the DOM).
    //  - Removing the jacket removes the whole group (jacket + mantra + placement).
    //  - Removing the mantra line removes the personalization add-ons (mantra +
    //    placement) and reverts the jacket to a standard line.
    if (cartItemRowToRemove) {
      const group = cartItemRowToRemove.dataset.personalizationGroup;
      const role = cartItemRowToRemove.dataset.personalizationRole;

      if (group) {
        // All lines in the group are rendered line items (the placement line is
        // hidden), so removal works purely off the group's rendered items.
        const groupRows = this.refs.cartItemRows.filter(
          (row) => row.dataset.personalizationGroup === group
        );

        if (role === 'jacket') {
          // Jacket removal drops every line in the group (jacket + mantra + placement).
          const keys = groupRows.map((row) => row.dataset.key).filter(Boolean);
          if (keys.length) {
            this.#removeLinesByKey(/** @type {string[]} */ (keys));
            this.#animateRemoveRows(groupRows);
            return;
          }
        } else {
          // Removing the mantra line drops the personalization add-ons (mantra +
          // placement) AND strips the personalization attributes off the jacket,
          // reverting it to a plain line with no leftover fulfillment data.
          const persoRows = groupRows.filter(
            (row) => row.dataset.personalizationRole === 'personalization'
          );
          const jacketKey = groupRows.find(
            (row) => row.dataset.personalizationRole === 'jacket'
          )?.dataset.key;
          const persoKeys = persoRows.map((row) => row.dataset.key).filter(Boolean);

          if (persoKeys.length && jacketKey) {
            this.#revertPersonalizedJacket(
              jacketKey,
              /** @type {string[]} */ (persoKeys),
              persoRows
            );
            return;
          }
          if (persoKeys.length) {
            this.#removeLinesByKey(/** @type {string[]} */ (persoKeys));
            this.#animateRemoveRows(persoRows);
            return;
          }
        }
      }
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    this.#animateRemoveRows(rowsToRemove);
  }

  /**
   * Animates and removes the given cart item rows from the DOM.
   * @param {HTMLLIElement[]} rows - The rows to remove.
   */
  #animateRemoveRows(rows) {
    rows.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Collects the comma-separated section ids to re-render on a cart mutation:
   * this component's section plus any other cart sections on the page.
   * @returns {string}
   */
  #collectSectionsToUpdate() {
    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    return Array.from(sectionsToUpdate).join(',');
  }

  /**
   * Applies a successful cart mutation response: resets the shimmer, refreshes
   * quantity selectors/totals, dispatches a CartUpdateEvent, morphs this section,
   * and reinitializes final-sale tooltips. Shared by updateQuantity and
   * #removeLinesByKey.
   * @param {any} parsedResponseText - The parsed /cart/*.js JSON response.
   */
  #applyCartUpdateResponse(parsedResponseText) {
    resetShimmer(this);

    const newSectionHTML = new DOMParser().parseFromString(
      parsedResponseText.sections[this.sectionId],
      'text/html'
    );
    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    this.#updateQuantitySelectors(parsedResponseText);

    this.dispatchEvent(
      new CartUpdateEvent(parsedResponseText, this.sectionId, {
        itemCount: newCartItemCount,
        source: 'cart-items-component',
        sections: parsedResponseText.sections,
      })
    );

    morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

    this.#updateCartQuantitySelectorButtonStates();
    this.#forceCartQuantityInputRepaint();

    setTimeout(() => {
      if (window.initFinalSaleTooltips) {
        window.initFinalSaleTooltips();
      }
    }, 50);
  }

  /**
   * VBR-220: removes multiple line items atomically by their line item keys
   * via /cart/update.js, then morphs all cart sections.
   * @param {string[]} keys - The line item keys to set to quantity 0.
   */
  #removeLinesByKey(keys) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('clear:user-action');

    this.#disableCartItems();

    const { cartTotal } = this.refs;

    /** @type {Record<string, number>} */
    const updates = {};
    for (const key of keys) updates[key] = 0;

    const body = JSON.stringify({
      updates,
      sections: this.#collectSectionsToUpdate(),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        this.#applyCartUpdateResponse(JSON.parse(responseText));
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * VBR-220: reverts a personalized jacket to a plain line when its personalization
   * is removed. Strips the personalization line item properties off the jacket
   * (so no stale mantra/placement/fulfillment data reaches the order), then removes
   * the paired mantra + hidden placement lines and morphs the cart once.
   * @param {string} jacketKey - The jacket line item key.
   * @param {string[]} persoKeys - The mantra + hidden placement line item keys.
   * @param {HTMLLIElement[]} persoRows - The rendered personalization rows to animate out.
   */
  async #revertPersonalizedJacket(jacketKey, persoKeys, persoRows) {
    this.#disableCartItems();

    try {
      invalidateCart();
      const cart = await getCart();
      const jacketLine = /** @type {any} */ (cart.items)?.find(
        (/** @type {any} */ item) => item.key === jacketKey
      );

      if (jacketLine) {
        /** @type {Record<string, string>} */
        const strippedProps = {};
        for (const [key, value] of Object.entries(jacketLine.properties || {})) {
          if (PERSONALIZATION_PROP_KEYS.has(key)) continue;
          strippedProps[key] = String(value);
        }

        // Replace the jacket line's properties with the personalization props removed.
        await fetch(
          `${Theme.routes.cart_change_url}`,
          fetchConfig('json', {
            body: JSON.stringify({
              id: jacketKey,
              quantity: jacketLine.quantity,
              properties: strippedProps,
            }),
          })
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      invalidateCart();
    }

    // Remove the mantra + placement lines and morph to the final state (jacket
    // now plain, add-ons gone).
    this.#removeLinesByKey(persoKeys);
    this.#animateRemoveRows(persoRows);
  }

  /**
   * Handles removing a discount from the cart (used by remove pill in cart summary/drawer).
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event on the remove button.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement)
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = getExistingDiscountCodes(this);
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: [this.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, config);
      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      const sectionHtml = data.sections?.[this.sectionId];
      if (sectionHtml) {
        morphSection(this.sectionId, sectionHtml);
      }
    } catch {
      // Silently ignore discount removal errors
    }
  };

  /**
   * Updates the quantity.
   * @param {Object} config
   * @param {number} config.line
   * @param {number} config.quantity
   * @param {string} config.action
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: this.#collectSectionsToUpdate(),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        if (parsedResponseText.errors) {
          resetShimmer(this);
          const errorMessage = parsedResponseText.errors;
          this.#handleCartError(line, parsedResponseText);
          // Refresh cart so displayed qty and totals match actual cart (e.g. when "only X amount added" limit is hit)
          const sectionHtml = parsedResponseText.sections?.[this.sectionId];
          if (sectionHtml) {
            const newSectionHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');
            const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
            const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;
            this.#updateQuantitySelectors(parsedResponseText);
            this.dispatchEvent(
              new CartUpdateEvent(parsedResponseText, this.sectionId, {
                itemCount: newCartItemCount,
                source: 'cart-items-component',
                sections: parsedResponseText.sections,
              })
            );
            morphSection(this.sectionId, sectionHtml);
            this.#updateCartQuantitySelectorButtonStates();
            this.#forceCartQuantityInputRepaint();
            // Re-show the error message after morph so the user still sees why qty was capped
            this.#showCartLineError(line, errorMessage);
          } else {
            sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
              this.#showCartLineError(line, errorMessage);
            });
          }
          setTimeout(() => {
            if (window.initFinalSaleTooltips) {
              window.initFinalSaleTooltips();
            }
          }, 50);
          return;
        }

        this.#applyCartUpdateResponse(parsedResponseText);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line
   * @param {Object} parsedResponseText
   * @param {string} parsedResponseText.errors
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Shows the error message for a cart line (e.g. after morph so message persists).
   * Queries the current DOM so it works after section content has been replaced.
   * @param {number} line - The line index (1-based).
   * @param {string} message - The error message to display.
   */
  #showCartLineError(line, message) {
    const container = this.querySelector(`[ref="cartItemErrorContainer-${line}"]`);
    const textEl = this.querySelector(`[ref="cartItemError-${line}"]`);
    if (container instanceof HTMLElement && textEl instanceof HTMLElement) {
      textEl.textContent = message;
      container.classList.remove('hidden');
    }
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      this.#forceCartQuantityInputRepaint();

      // Reinitialize final sale tooltips after morphing
      setTimeout(() => {
        if (window.initFinalSaleTooltips) {
          window.initFinalSaleTooltips();
        }
      }, 50);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /** Disables the cart items. */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /** Enables the cart items. */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /** Updates button states for all cart quantity selector components. */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /** Force cart quantity inputs to repaint (iOS Safari/Chrome). */
  #forceCartQuantityInputRepaint() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).forceQuantityInputRepaint?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
